import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Select } from '@mui/material';
import Chip from '@mui/material/Chip';
import { useState, useEffect, useRef } from 'react';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { Text } from '../../Atoms/Text';
import { createNewPayment, Payment } from '../../../store/Payment';
import { EntityType, SearchResult } from '../../../store/Search';
import { SearchBox } from '../../Compounds/Search/Search';
import { Asset } from '../../../store/Asset';
import { getAsset } from '../../../Api/Asset/AssetApi';
import FinancialInputField from '../../Compounds/DetailPanel/EditModal/FinancialInputField';
import { DateEditComponent } from '../../Compounds/DetailPanel/Inputs/DateEditComponent';
import { chipStyles, getUTCDate } from '../../../utils/helpers';
import { PaymentRecipientType } from '../../../store/PaymentRecipient';
import { Icon } from '../../Atoms/Icon';
import { hasEngineerOrHigherPermissions } from '../../../store/User';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { RecordLink } from '../../Atoms/RecordLink';

interface NewPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  initialAsset: Asset | null;
}

interface FormData {
  assetId: string | null;
  recipientId: string | null;
  amount: number | null;
  date: string | null;
  assetName: string | null;
  recipientName: string | null;
  recipient: { name: string; type: PaymentRecipientType } | null;
}

const AssetInfo = ({ asset }: { asset: Asset | null }) => {
  if (!asset) return null;
  const status = asset.commissionsStatus || 'N/A';
  return (
    <>
      <Text
        size="sm14"
        color="grey6"
        className="mt-2">
        Commissions Status
      </Text>
      <div className="w-content">
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      </div>
      <Text
        size="sm14"
        color="grey6"
        className="mt-2">
        Supplier
      </Text>
      <Text size="sm14">{asset.supplierName}</Text>
      <Text
        size="sm14"
        color="grey6"
        className="mt-2">
        Sales Agent
      </Text>
      <Text size="sm14">{asset.salesAgent}</Text>
      <Text
        size="sm14"
        color="grey6"
        className="mt-2">
        Customer
      </Text>
      <Text size="sm14">
        <RecordLink
          className="flex space-x-1 w-full text-indigo"
          to={`/customer/${asset.accountGri}`}
          title={asset.customer}>
          {asset.customer}
        </RecordLink>
      </Text>
    </>
  );
};

export const NewPaymentDialog = ({ open, onClose, initialAsset }: NewPaymentDialogProps) => {
  const defaultFormData: FormData = {
    assetId: initialAsset ? initialAsset.id : null,
    recipientId: null,
    amount: null,
    date: getUTCDate(new Date()).toISOString(),
    recipient: null,
    assetName: null,
    recipientName: null
  };
  const [loading, setLoading] = useState(false);
  const [dataForm, setDataForm] = useState<FormData>(defaultFormData);
  const dataFormRef = useRef<FormData>(defaultFormData);
  const [asset, setAsset] = useState<Asset | null>(initialAsset);
  const [recipient, setRecipient] = useState<{ name: string; type: PaymentRecipientType | null }>({
    name: '',
    type: null
  });
  const [isNewRecipient, setIsNewRecipient] = useState<boolean>(false);
  const { setSnack } = useSnackbar();

  const updateDataForm = (update: FormData | ((previous: FormData) => FormData)) => {
    const next = typeof update === 'function' ? update(dataFormRef.current) : update;
    dataFormRef.current = next;
    setDataForm(next);
  };

  const resetForm = () => {
    updateDataForm(defaultFormData);
    setAsset(initialAsset);
    setRecipient({ name: '', type: null });
    setIsNewRecipient(false);
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (initialAsset) return;

    const fetchData = async () => {
      if (dataForm.assetId) {
        const { data: assetData } = await getAsset(dataForm.assetId);
        setAsset(assetData);
      } else {
        setAsset(null);
      }
    };

    fetchData();
  }, [dataForm.assetId]);

  const recipientValid = isNewRecipient
    ? recipient.name.trim() !== '' && recipient.type !== null
    : dataForm.recipientId !== null;

  const isFormValid = (form: FormData) =>
    form.assetId !== null && form.amount !== null && form.date !== null && recipientValid;
  const disabled = !isFormValid(dataForm) || loading;

  const handleSelect = (result: SearchResult) => {
    if (!result) return;
    const { source, id, title } = result;
    updateDataForm((prevData) => {
      const newData = { ...prevData };
      const object = source === EntityType.ASSET ? 'asset' : 'recipient';
      newData[`${object}Id`] = id;
      newData[`${object}Name`] = title;
      return newData;
    });
  };

  const handleClearSelect = (object: string) => {
    updateDataForm((prevData) => ({ ...prevData, [`${object}Id`]: null, [`${object}Name`]: null }));
  };

  const handleSave = async (close = true) => {
    const currentForm = dataFormRef.current;
    if (!isFormValid(currentForm) || loading) return;
    setLoading(true);
    const newPayment = {
      assetId: currentForm.assetId,
      amount: currentForm.amount,
      date: currentForm.date,
      ...(isNewRecipient ? { newRecipient: recipient } : { recipientId: currentForm.recipientId })
    } as unknown as Payment;
    const result = await createNewPayment(newPayment);
    setLoading(false);
    if (result?.error) return;
    setSnack({ message: 'Payment created successfully', type: 'success', open: true });
    if (close) {
      onClose();
    } else {
      resetForm();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmitAndAddAnother = async () => {
    if (disabled) return;
    handleSave(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const doNothing = () => {};

  const switchRecipientMode = () => {
    if (!hasEngineerOrHigherPermissions()) return null;
    const label = isNewRecipient ? 'Find Recipient' : 'New Recipient';

    const handleClick = () => {
      if (isNewRecipient) {
        updateDataForm((prevData) => ({ ...prevData, recipientId: null, recipientName: null }));
      } else {
        setRecipient({ name: '', type: null });
      }
      setIsNewRecipient((prev) => !prev);
    };
    return (
      <button
        className="flex items-center space-x-2"
        onClick={handleClick}>
        {!isNewRecipient && <Icon type="plus" />}
        <Text
          color="indigo"
          size="sm14">
          {label}
        </Text>
      </button>
    );
  };

  const recipientInfo = () => {
    if (isNewRecipient) {
      return (
        <>
          <Text
            size="sm14"
            color="grey6"
            className="mt-2">
            New Recipient Name
          </Text>
          <div className="flex flex-row items-center gap-x-4">
            <TextField
              className="w-3/5"
              value={recipient.name}
              data-testid="new-recipient-name-input"
              onChange={(e) => setRecipient({ ...recipient, name: e?.target?.value })}
              onBlur={() => setRecipient((prev) => ({ ...prev, name: prev.name.trim() }))}
              size="small"
            />
            {switchRecipientMode()}
          </div>
          <Text
            size="sm14"
            color="grey6"
            className="mt-2">
            New Recipient Type
          </Text>
          <Select
            value={recipient.type}
            onChange={(e) => {
              const typeValue = e?.target?.value as PaymentRecipientType;
              setRecipient({ ...recipient, type: typeValue });
            }}
            size="small"
            className="w-3/5"
            data-testid="new-recipient-type-select">
            {Object.values(PaymentRecipientType).map((typeOption) => (
              <MenuItem
                key={typeOption}
                value={typeOption}>
                {typeOption}
              </MenuItem>
            ))}
          </Select>
        </>
      );
    } else {
      return (
        <>
          <Text
            size="sm14"
            color="grey6"
            className="mt-2">
            Recipient
          </Text>
          <div className="flex flex-row items-center gap-x-4">
            <div className="w-3/5">
              <SearchBox
                fullWidth
                fromModal
                handleSelect={handleSelect}
                handleClearSelect={() => handleClearSelect('recipient')}
                searchEntityType={EntityType.PAYMENT_RECIPIENT}
                dropdownZIndex={1300}
              />
            </div>
            {switchRecipientMode()}
          </div>
        </>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.SubmitEvent) => {
            event.preventDefault();
            handleSave();
          },
          sx: { overflowY: 'visible' }
        }
      }}
      fullWidth
      maxWidth="md"
      data-testid="create-payment-modal">
      <DialogTitle>
        <Text
          weight="bold"
          size="lg"
          color="grey9">
          New Payment
        </Text>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 355, overflowY: 'visible', paddingLeft: 3, paddingRight: 3 }}>
        {loading && <ComponentSpinner label="Saving..." />}
        {!loading && (
          <div className="flex flex-row gap-x-4 w-full mx-6">
            <div className="flex flex-col gap-y-2 w-1/2">
              <Text
                size="sm14"
                color="grey6">
                Asset
              </Text>
              {initialAsset ? (
                <div className="flex gap-1">
                  <Icon
                    className="p-2"
                    type="assetMed"
                  />
                  {`${asset?.supplierName}: ${asset?.number}`}
                </div>
              ) : (
                <div className="w-4/5">
                  <SearchBox
                    fromModal
                    fullWidth
                    handleSelect={handleSelect}
                    handleClearSelect={() => handleClearSelect('asset')}
                    searchEntityType={EntityType.ASSET}
                    dropdownZIndex={1300}
                  />
                </div>
              )}
              <AssetInfo asset={asset} />
            </div>
            <div className="flex flex-col gap-y-2 w-1/2">
              <Text
                size="sm14"
                color="grey6">
                Date
              </Text>
              <div className="w-1/2">
                <DateEditComponent
                  label="Date"
                  val={dataForm.date}
                  setLocalVal={doNothing}
                  handleClose={doNothing}
                  data-testid="payment-date-input"
                  handler={(v: string | null) => {
                    updateDataForm((prevData) => ({ ...prevData, date: v }));
                  }}
                />
              </div>
              <Text
                size="sm14"
                color="grey6"
                className="mt-2 -mb-2">
                Amount
              </Text>
              <div className="w-1/2">
                <FinancialInputField
                  value={dataForm.amount || ''}
                  label=""
                  name="amount"
                  onChange={() => (e: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    const amount = value ? parseFloat(value) : null;
                    updateDataForm((prevData) => ({ ...prevData, amount }));
                  }}
                />
              </div>
              {recipientInfo()}
            </div>
          </div>
        )}
      </DialogContent>
      <hr className="mb-1 mx-6" />
      {!loading && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            data-testid="save-and-add-another-button"
            disabled={disabled}
            onClick={handleSubmitAndAddAnother}>
            Save and Add Another
          </Button>
          <Button
            disabled={disabled}
            type="submit"
            variant="contained">
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
